<template>
  <el-dialog
    :visible="visible"
    width="1100px"
    title="风控信息"
    :close-on-click-modal="false"
    @close="handleClose"
    top="5vh"
  >
    <div v-loading="loading" class="dialog-content">
      <div class="section-title">提现订单数据</div>
      <el-descriptions
        :column="3"
        border
        size="small"
        class="desc"
        :contentStyle="{ width: '22%', padding: '4px 10px' }"
        :labelStyle="{ width: '200px' }"
      >
        <el-descriptions-item label="订单号">{{ dataView.order_id || '--' }}</el-descriptions-item>
        <el-descriptions-item label="订单状态">{{ statusText }}</el-descriptions-item>
        <el-descriptions-item label="提款金额">{{ formatMoney(dataView.amount) }}</el-descriptions-item>
        <el-descriptions-item label="预计到账">{{ formatMoney(dataView.estimated_amount) }}</el-descriptions-item>
        <el-descriptions-item label="实际到账">{{ formatMoney(dataView.real_amount) }}</el-descriptions-item>
        <el-descriptions-item label="总手续费">{{ formatMoney(dataView.total_fee) }}</el-descriptions-item>
        <el-descriptions-item label="代付通道">{{ dataView.payment_channel_name || '--' }}</el-descriptions-item>
        <el-descriptions-item label="提款账号">{{ dataView.account || '--' }}</el-descriptions-item>
        <!-- <el-descriptions-item label="真实姓名">{{ dataView.realname || '--' }}</el-descriptions-item> -->
      </el-descriptions>

      <div class="section-title" style="margin-top: 12px">会员汇总数据</div>
      <el-descriptions
        :column="3"
        border
        size="small"
        class="desc"
        :contentStyle="{ width: '22%', padding: '4px 10px' }"
        :labelStyle="{ width: '200px' }"
      >
        <el-descriptions-item label="会员ID">{{ dataView.uid ?? '--' }}</el-descriptions-item>
        <el-descriptions-item label="会员账号">{{ dataView.username || '--' }}</el-descriptions-item>
        <el-descriptions-item label="VIP等级">
          <span v-if="dataView.vip_level != null">VIP{{ dataView.vip_level }}</span>
          <span v-else>--</span>
        </el-descriptions-item>
        <el-descriptions-item label="账户余额">{{ formatMoney(dataView.gold) }}</el-descriptions-item>
        <el-descriptions-item label="上级代理">{{ dataView.inviter_uid ?? '--' }}</el-descriptions-item>
        <el-descriptions-item label="注册来源">{{ registerTypeText }}</el-descriptions-item>
        <el-descriptions-item label="会员标签">{{ dataView.vip_tags || '--' }}</el-descriptions-item>
        <el-descriptions-item label="同IP人数">{{ dataView.same_ip_num ?? '--' }}</el-descriptions-item>
        <el-descriptions-item label="累计充值"
          >{{ formatMoney(dataView.total_payin_amount) }}（{{
            dataView.total_payin_times ?? 0
          }}次）</el-descriptions-item
        >
        <el-descriptions-item label="累计提款"
          >{{ formatMoney(dataView.total_payout_amount) }}（{{
            dataView.total_payout_times ?? 0
          }}次）</el-descriptions-item
        >
        <el-descriptions-item label="今日充值"
          >{{ formatMoney(dataView.today_payin_amount) }}（{{
            dataView.today_payin_times ?? 0
          }}次）</el-descriptions-item
        >
        <el-descriptions-item label="今日提款"
          >{{ formatMoney(dataView.today_payout_amount) }}（{{
            dataView.today_payout_times ?? 0
          }}次）</el-descriptions-item
        >
        <el-descriptions-item label="活动奖励">{{ formatMoney(dataView.reward_amount) }}</el-descriptions-item>
        <el-descriptions-item label="累计盈亏/损益">{{ formatMoney(dataView.total_earn) }}</el-descriptions-item>
        <el-descriptions-item label="上次充值时间">{{ formatTs(dataView.last_payin_at) }}</el-descriptions-item>
        <el-descriptions-item label="上次提款时间">{{ formatTs(dataView.last_payout_at) }}</el-descriptions-item>
      </el-descriptions>

      <div class="section-title" style="margin-top: 12px">统计周期数据</div>
      <div class="table-container">
        <SchemaTablePage
          ref="tablePageRef"
          layout-mode="dialog"
          :search-schema="[]"
          :columns="columns"
          :default-query="{}"
          :request="request"
          :build-params="buildParams"
          :parse-result="parseResult"
          :table-attrs="{ size: 'mini' }"
          :immediate="false"
        >
          <template #colLogType="{ row }">{{ logTypeText(row.log_type) }}</template>
          <template #colPayin="{ row }">{{ formatMoney(row.payin_amount) }}</template>
          <template #colPayout="{ row }">{{ formatMoney(row.payout_amount) }}</template>
          <template #colBet="{ row }">{{ formatMoney(row.bet_amount) }}</template>
          <template #colWinloss="{ row }">{{ formatMoney(row.winloss) }}</template>
          <template #colEarn="{ row }">{{ formatMoney(row.earn) }}</template>
        </SchemaTablePage>
      </div>
    </div>

    <template #footer>
      <!-- <el-button size="small" @click="handleClose">关闭</el-button> -->
      <el-button size="small" type="danger" :loading="auditing" :disabled="auditing" @click="handleAudit(0)"
        >不通过</el-button
      >
      <el-button size="small" type="primary" :loading="auditing" :disabled="auditing" @click="handleAudit(1)"
        >通过</el-button
      >
    </template>
  </el-dialog>
</template>

<script setup>
import { getPayoutApproveOverview, payoutApprove } from '@/api/finance/moneyManagement'
import SchemaTablePage from '@/components/SchemaTablePage'
import { centsToYuan } from '@/utils/amount'
import { formatTimestamp } from '@/utils/time'
import { WITHDRAW_PENDING_STATUS_MAP } from './config'
import { computed, getCurrentInstance, nextTick, ref, watch } from 'vue'

// 1 普通注册，2 后台注册，3 分享注册
const registerTypeMap = {
  1: '投放渠道',
  2: '邀请注册',
  3: '普通注册',
}

const props = defineProps({
  visible: { type: Boolean, default: false },
  row: { type: Object, default: null },
})
const emit = defineEmits(['update:visible', 'audited'])

const { proxy } = getCurrentInstance()

const loading = ref(false)
const rawData = ref({})
const auditing = ref(false)
const tablePageRef = ref(null)

const dataView = computed(() => rawData.value || {})
const statusText = computed(() => {
  const s = dataView.value?.status
  if (s == null) return '--'
  return WITHDRAW_PENDING_STATUS_MAP[s] != null ? WITHDRAW_PENDING_STATUS_MAP[s] : `状态${s}`
})
const registerTypeText = computed(() => {
  const t = dataView.value?.register_type
  const v = registerTypeMap[t]
  if (v == null || v === '') return '--'
  // 与会员概览一致：type=1 追加 ads_channel
  return t === 1 ? v + ' ' + (dataView.value?.ads_channel || '') : v
})

const columns = [
  { prop: 'log_type', label: '统计周期', minWidth: 120, resizable: false, slot: 'colLogType' },
  { prop: 'payin_amount', label: '充值金额', minWidth: 120, resizable: false, slot: 'colPayin' },
  { prop: 'payout_amount', label: '提现金额', minWidth: 120, resizable: false, slot: 'colPayout' },
  { prop: 'bet_amount', label: '投注金额', minWidth: 120, resizable: false, slot: 'colBet' },
  { prop: 'winloss', label: '盈/输金额', minWidth: 120, resizable: false, slot: 'colWinloss' },
  { prop: 'earn', label: '充值差额', minWidth: 120, resizable: false, slot: 'colEarn' },
  { prop: 'bet_times', label: '注单数', minWidth: 90, resizable: false },
]

function logTypeText(t) {
  if (t === 1) return '今天'
  if (t === 2) return '近3天'
  return t != null ? String(t) : '--'
}

function formatMoney(v) {
  if (v == null || v === '') return '--'
  const n = Number(v)
  if (Number.isNaN(n)) return String(v)
  return centsToYuan(n)
}

function formatTs(ts) {
  if (!ts) return '--'
  return formatTimestamp(ts)
}

async function fetchData() {
  const orderId = props.row?.orderNo || props.row?.order_id
  if (!orderId) return
  loading.value = true
  try {
    const res = await getPayoutApproveOverview({ order_id: orderId })
    rawData.value = res?.data || {}
    await nextTick()
    tablePageRef.value?.reload?.()
  } catch (e) {
    console.error(e)
    rawData.value = {}
    proxy?.$message?.error('获取风控信息失败')
  } finally {
    loading.value = false
  }
}

function request() {
  const list = Array.isArray(rawData.value?.list) ? rawData.value.list : []
  return Promise.resolve({
    code: 0,
    data: {
      list,
      total: list.length,
    },
    msg: 'ok',
  })
}

function buildParams() {
  return {}
}

function parseResult(res) {
  const resp = (res && res.data) || {}
  return {
    list: Array.isArray(resp.list) ? resp.list : [],
    total: resp.total || 0,
  }
}

function handleClose() {
  emit('update:visible', false)
}

async function handleAudit(status) {
  const orderId = props.row?.orderNo || props.row?.order_id
  if (!orderId) return
  auditing.value = true
  try {
    const res = await payoutApprove({ order_id: orderId, status })
    if (res.code === 0) {
      proxy?.$message?.success(status === 1 ? '审核通过成功' : '审核不通过成功')
      emit('audited')
      handleClose()
    } else {
      proxy?.$message?.error(status === 1 ? '审核通过失败' : '审核不通过失败')
    }
  } catch (e) {
    console.error(e)
    proxy?.$message?.error(status === 1 ? '审核通过失败' : '审核不通过失败')
  } finally {
    auditing.value = false
  }
}

watch(
  () => props.visible,
  v => {
    if (v) fetchData()
  }
)
</script>

<style lang="scss" scoped>
.dialog-content {
  max-height: 70vh;
  overflow-y: auto;
}
.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}

.desc {
  background: #fff;
}
.table-container {
  height: 180px;
  overflow-y: auto;
  :deep(.pagination-container) {
    display: none;
  }
}
</style>
